import './style.scss'
import { Button, Drawer, Input, message, Modal, notification, Popconfirm, Progress, Radio, Space } from 'antd'
import { RcFile } from 'antd/es/upload'
import Dragger from 'antd/es/upload/Dragger'
import axios from 'axios'
import { FC, memo, Suspense, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cmsApi } from '@/api'
import * as Api from '@/api/account'
import * as homeApi from '@/api/home'
import SystemDeclare from '@/components/Declare'
import PayPointModal from '@/components/PayPointModal'
import { IUserPackage, UserStore } from '@/global-states'
import { eventTracking, showDeclare, showPlanModal } from '@/libs/util'
import { CloudUploadOutlined, DeleteOutlined, ExclamationCircleFilled, LoadingOutlined } from '@ant-design/icons'

interface IProps {
  open: boolean
  onCancel?: () => void
  onOk?: () => void
  group?: any
}

const CloneHighDrawer: FC<IProps> = (props) => {
  const { t: translate } = useTranslation(['voice', 'system'])
  const { open, onCancel, onOk, group } = props
  const { declareChecked, userPackage } = UserStore
  const [name, setName] = useState('')
  const [audioUrl, setAudioUrl] = useState<string>(undefined as any)
  const [audioInfo, setAudioInfo] = useState<any>({})
  const [percent, setPercent] = useState(-1)
  const [loading, setLoading] = useState(false)
  const cancelTokenSource = useRef<any>()
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previeUrl, setPreviewUrl] = useState('')
  const audioRef = useRef<any>()
  const [clonePreflight, setClonePreflight] = useState<any>()
  const [payPointModalOpen, setPayPointModalOpen] = useState(false)
  const [showGuide, setShowGuide] = useState<any>(false)
  const [cloneableVoices, setCloneableVoices] = useState<any[]>([])
  const [cloneId, setCloneId] = useState<any>()
  const [restartCloneNum, setRestartCloneNum] = useState(0)
  const [cloneConfirmDisabled, setCloneConfirmDisabled] = useState(false)
  const [title, setTitle] = useState('')
  const [model, setModel] = useState(2)

  useEffect(() => {
    if (open) {
      getClonePreflight()
      setPreviewLoading(false)
      setPreviewUrl('')
      setName('')
      resetUploadAudio()
      setTitle('')
      setModel(2)
      setShowGuide(true)
      setCloneId(undefined)
    }
  }, [open])

  const getClonePreflight = async () => {
    const res = await Api.clonePreflight({
      level: 20
    })
    setClonePreflight(res || {})
  }

  const getCloneableVoices = async (refresh?: boolean) => {
    const res = await homeApi.getCloneableVoices()
    setCloneableVoices(res?.list || [])
    setCloneId(res?.list?.[0]?.id)
    setRestartCloneNum(res?.list?.[0]?.remained_demo_tries || 0)
    if (refresh) return
    return res
  }

  const beforeAudioUpload = async (file: RcFile) => {
    return new Promise((resolve) => {
      const type = file?.name?.split(/\./)?.slice(-1)?.[0]?.toLocaleLowerCase() as string
      if (['wav', 'mp3', 'm4a'].includes(type)) {
        const size = file?.size || 0
        if (size / 1000 / 1000 > 20) {
          message.warning(translate('voice.audio_size_warning'))
          return resolve(false)
        }
        const audioElement = document.createElement('audio')
        audioElement.src = URL.createObjectURL(file)
        audioElement.addEventListener('loadedmetadata', () => {
          console.log('videoElement', audioElement.duration)
          if (audioElement.duration < 10 || audioElement.duration > 180) {
            message.warning(translate('voice.audio_duration_warning_premium'))
            return resolve(false)
          }
          resolve(true)
        })
      } else {
        message.warning(translate('voice.audio_format_warning'))
        resolve(false)
      }
    })
  }

  const onDrop = async (file: RcFile) => {
    const type = file?.name?.split(/\./)?.slice(-1)?.[0]?.toLocaleLowerCase() as string
    if (!['wav', 'mp3', 'm4a'].includes(type)) {
      message.warning(translate('voice.audio_format_warning'))
    }
  }

  const uploadFile = async (file: RcFile) => {
    try {
      const segs = (file.name || '').split(/\./)
      const { upload_url, oss_key, content_type, public_url } =
        (await cmsApi.post('upload_url', {
          extension: segs[segs.length - 1],
          media_type: 2
        })) || {}
      if (!upload_url) {
        throw new Error(translate('system.upload_failed'))
      }

      cancelTokenSource.current = axios.CancelToken.source()

      setPercent(0)

      await cmsApi.upload(upload_url.replace(/^http:\/\//, 'https://').replace('-internal', ''), file, {
        onUploadProgress: (progress) => {
          const percent = Math.round((progress.progress || 0) * 100)
          setPercent(percent)
        },
        headers: {
          'Content-Type': content_type
        },
        cancelToken: cancelTokenSource.current?.token
      })
      setAudioInfo({
        title: segs[0],
        oss_key,
        public_url
      })
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        message.error(err?.message || err)
      }
    }
  }

  const resetUploadAudio = () => {
    setPercent(-1)
    setAudioUrl('')
    setAudioInfo({})
    setPreviewUrl('')
  }

  const cancelUpload = () => {
    if (cancelTokenSource) {
      cancelTokenSource.current?.cancel(translate('system.cancel_upload'))
      resetUploadAudio()
    }
  }

  const previewDemoConfirm = async () => {
    if (!declareChecked) {
      await showDeclare()
    }

    if (!audioInfo.oss_key) {
      return message.warning(translate('voice.upload_reference_audio'))
    }

    if (!cloneableVoices?.length) {
      setCloneConfirmDisabled(true)
      const res: IUserPackage = await UserStore.updateUserPackage()
      const totalPoint = (res.membership_credits || 0) + (res.permanent_credits || 0) + (res.free_credits || 0)
      setCloneConfirmDisabled(false)
      Modal.confirm({
        title: translate('voice.points_consumption_confirmation'),
        content: (
          <div>
            <div>
              {translate('voice.points_consumption_text', {
                points: clonePreflight?.credits_cost || 0
              })}
            </div>
            <div>
              <strong>
                {translate('voice.your_points_balance')}: {totalPoint}
                {translate('voice.points')}
              </strong>
            </div>
            <div>
              <strong>
                {translate('voice.this_consumption')}:
                <label className="red">
                  {clonePreflight?.credits_cost || 0}
                  {translate('voice.points')}
                </label>
              </strong>
            </div>
          </div>
        ),
        okText: translate('voice.confirm_purchase'),
        cancelText: translate('system.cancel'),
        onOk: () => {
          previewDemo()
        }
      })
    } else {
      previewDemo()
    }
  }

  const previewDemo = async () => {
    eventTracking('InstantClonePremiumPreviewClick', {
      ossKey: audioInfo.oss_key
    })
    setPreviewLoading(true)

    try {
      if (cloneId) {
        const res: any = await homeApi.startCloneVoices(cloneId, {
          audio_oss_key: audioInfo.oss_key
        })
        setPreviewUrl(res.demo_url)
        setRestartCloneNum(res.remained_demo_tries || 0)
      } else {
        const { id } = await homeApi.buyCloneVoices()
        setCloneId(id)
        const res: any = await homeApi.startCloneVoices(id, {
          audio_oss_key: audioInfo.oss_key
        })
        setPreviewUrl(res.demo_url)
        setRestartCloneNum(res.remained_demo_tries || 0)
      }
    } catch (error: any) {
      if (error.code === 1002) {
        setPayPointModalOpen(true)
      }
    } finally {
      setPreviewLoading(false)
    }
  }

  const nextStep = () => {
    setShowGuide(false)
  }

  const restartClone = () => {
    Modal.confirm({
      title: translate('system.tip'),
      content: (
        <div>
          {translate('voice.restart_clone_message', {
            count: restartCloneNum
          })}
          <span className="red">{translate('voice.restart_clone_warning')}</span>
        </div>
      ),
      okText: translate('voice.confirm_restart_clone'),
      cancelText: translate('system.cancel'),
      onOk: () => {
        resetUploadAudio()
        getCloneableVoices(true)
      }
    })
  }

  const checkAvalid = async () => {
    if (!declareChecked) {
      await showDeclare()
    }

    if (!name?.trim() && !group?.id) {
      message.warning(translate('voice.input_voice_name'))
      return false
    }

    if (group?.id && !title) {
      message.warning(translate('voice.input_style_name'))
      return false
    }

    if (!audioInfo.oss_key && !cloneId) {
      message.warning(translate('voice.upload_reference_audio'))
      return false
    }

    return true
  }

  const onConfirm = async () => {
    if (!(await checkAvalid())) return

    eventTracking('InstantClonePremiumConfirm')
    Modal.confirm({
      title: translate('system.tip'),
      content: (
        <div>
          {translate('voice.confirm_clone_message')}
          <span className="red">{translate('voice.confirm_clone_warning')}</span>
        </div>
      ),
      okText: translate('voice.confirm_complete_clone'),
      cancelText: translate('voice.confirm_effect'),
      onCancel: () => {
        eventTracking('InstantClonePremiumCheck')
        audioRef.current.play()
      },
      onOk: async () => {
        eventTracking('InstantClonePremiumConfirmed')
        setLoading(true)
        try {
          const params: any = {
            with_group: {
              new_group_title: name
            }
          }

          if (group?.id) {
            params.voice_name = title
            params.with_group = {
              group_id: group.id
            }
          }
          await homeApi.completeCloneVoices(cloneId, params)
          notification.success({
            message: translate('voice.clone_in_progress'),
            description: translate('voice.clone_success_message')
          })
          onCancel?.()
          onOk?.()
        } finally {
          setLoading(false)
        }
      }
    })
  }

  const cloneVoiceV2 = async () => {
    if (!(await checkAvalid())) return

    try {
      setCloneConfirmDisabled(true)
      const params: any = {
        audio_oss_key: [audioInfo.oss_key],
        level: 10,
        with_group: {
          new_group_title: name
        },
        tts_model: 8
      }

      if (group?.id) {
        params.voice_name = title
        params.with_group = {
          group_id: group.id
        }
      }
      await homeApi.voiceClone(params)
      notification.success({
        message: translate('voice.clone_complete'),
        description: translate('voice.clone_success_message')
      })
      onCancel?.()
      onOk?.()
    } finally {
      setCloneConfirmDisabled(false)
    }
  }

  const onModelChange = async (e: any) => {
    const v = e.target.value
    if (v === 1) {
      if ((userPackage?.current_membership_level || 0) < 20) {
        eventTracking('HighvoiceCloneLimit')
        return Modal.confirm({
          title: translate('voice.membership_level_not_enough'),
          content: <div>{translate('voice.membership_level_message')}</div>,
          okText: translate('voice.upgrade_membership'),
          cancelText: translate('system.cancel'),
          onOk: () => {
            showPlanModal()
          }
        })
      }

      const res = await getCloneableVoices()
      if (res?.list?.[0]?.last_demo_source_audio) {
        const lastDemo = res?.list?.[0]
        return Modal.confirm({
          title: translate('system.tip'),
          content: translate('voice.unfinished_clone_message'),
          okText: translate('voice.go_complete_clone'),
          onOk: () => {
            setModel(1)
            setAudioUrl(lastDemo.last_demo_source_audio)
            setPreviewUrl(lastDemo.last_demo_result_audio)
            setPercent(100)
            UserStore.declareChecked = true
          }
        })
      }
    }
    setCloneId(undefined)
    setModel(e.target.value)
  }

  return (
    <Suspense>
      <Drawer
        className="create-high-drawer"
        title={translate('voice.voice_clone_premium')}
        width={720}
        onClose={onCancel}
        open={open}
        footer={
          <>
            {showGuide ? <div className="declare"></div> : <SystemDeclare />}
            <Space>
              {showGuide ? (
                <Button type="primary" onClick={nextStep}>
                  {translate('system.next_step')}
                </Button>
              ) : (
                <>
                  {model === 2 ? (
                    <>
                      <div className="fee">
                        <label className={`light`}>{translate('voice.free_trial')}</label>
                      </div>
                      <Button type="primary" onClick={cloneVoiceV2} loading={cloneConfirmDisabled}>
                        {translate('voice.start_clone')}
                      </Button>
                    </>
                  ) : (
                    <>
                      {previeUrl ? (
                        <>
                          <div className="fee">
                            <div className="num gray">
                              {translate('voice.remaining_modifications')}: {restartCloneNum}
                            </div>
                          </div>
                          <Button onClick={restartClone} disabled={restartCloneNum < 1}>
                            {translate('voice.restart_clone')}
                          </Button>
                          <Button type="primary" onClick={onConfirm} loading={loading}>
                            {translate('voice.complete_clone')}
                          </Button>
                        </>
                      ) : (
                        <>
                          <div className="fee">
                            <div className="score">
                              <label className={`light ${cloneableVoices?.length > 0 ? 'through' : 'gradient'}`}>
                                {translate('voice.consume_points', {
                                  points: clonePreflight?.credits_cost || 0
                                })}
                              </label>
                              {clonePreflight?.limited_time_free && (
                                <label className="light">{translate('voice.limited_time_free')}</label>
                              )}
                            </div>
                            <div className={`num ${cloneableVoices?.length > 0 ? 'light' : 'gray'}`}>
                              {translate('voice.remaining_free_times')}: {cloneableVoices?.length || 0}
                            </div>
                          </div>
                          <Button
                            type="primary"
                            onClick={previewDemoConfirm}
                            loading={previewLoading || cloneConfirmDisabled}
                          >
                            {translate('voice.start_clone')}
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </Space>
          </>
        }
      >
        <div className="main">
          {showGuide ? (
            <div className="form-item">
              <div className="title">{translate('voice.clone_process')}</div>
              <div className="desc-list">
                <div className="list-item">
                  <div className="list-title">
                    <strong>1. {translate('voice.upload_reference_audio')}</strong>
                  </div>
                  <div className="list-content">
                    <div>{translate('voice.upload_audio_instruction')}</div>
                    <div>
                      （1）<strong>{translate('voice.audio_duration')}：</strong>
                      {translate('voice.audio_duration_tip_premium')}
                    </div>
                    <div>
                      （2）<strong>{translate('voice.audio_quality')}：</strong>
                    </div>
                    <ul>
                      <li>{translate('voice.audio_quality_tip1')}</li>
                      <li>{translate('voice.audio_quality_tip2')}</li>
                      <li>{translate('voice.audio_quality_tip3')}</li>
                      <li>{translate('voice.audio_quality_tip4')}</li>
                      <li>{translate('voice.audio_quality_tip5')}</li>
                      <li>{translate('voice.audio_quality_tip6')}</li>
                    </ul>
                    <div>
                      （3）<strong>{translate('voice.audio_content')}：</strong>
                    </div>
                    <ul>
                      <li>
                        {translate('voice.audio_content_tip1')}
                        <span className="red">{translate('voice.audio_content_tip2')}</span>
                      </li>
                    </ul>
                    <div>
                      （4）<strong>{translate('voice.audio_format')}：</strong>
                    </div>
                    <ul>
                      <li>{translate('voice.audio_format_tip')}</li>
                    </ul>
                  </div>
                </div>

                <div className="list-item">
                  <div className="list-title">
                    <strong>2. {translate('voice.points_consumption')}</strong>
                  </div>
                  <div className="list-content">
                    <div>
                      {translate('voice.premium_points_consumption')}: <strong>5000 {translate('voice.points')}</strong>
                    </div>
                    <div>{translate('voice.points_deduction_message')}</div>
                    <div className="red">{translate('voice.special_note')}</div>
                    <div>{translate('voice.no_refund_policy')}</div>
                  </div>
                </div>

                <div className="list-item">
                  <div className="list-title">
                    <strong>3. {translate('voice.preview_effect')}</strong>
                  </div>
                  <div className="list-content">
                    <div>{translate('voice.preview_instruction')}</div>
                    <div>
                      {translate('voice.modification_chances', {
                        count: 2
                      })}
                    </div>
                    <div>{translate('voice.audio_quality_advice')}</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="form-item">
                <div className="title">{translate('voice.voice_name')}</div>
                <Input
                  maxLength={20}
                  placeholder={translate('voice.input_voice_name')}
                  value={group?.title || name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={group?.id}
                />
              </div>

              {group?.id && (
                <div className="form-item">
                  <div className="title">{translate('voice.style_name')}</div>
                  <Input
                    maxLength={20}
                    placeholder={translate('voice.input_style_placeholder')}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>
              )}
              <div className="form-item">
                <div className="title">
                  {translate('voice.reference_audio')}
                  <Popconfirm
                    title={translate('system.tip')}
                    description={
                      <div className="popconfirm-list" style={{ width: 620 }}>
                        <div>
                          （1）<strong>{translate('voice.audio_duration')}：</strong>
                          {translate('voice.audio_duration_tip_premium')}
                        </div>
                        <div>
                          （2）<strong>{translate('voice.audio_quality')}：</strong>
                        </div>
                        <ul>
                          <li>{translate('voice.audio_quality_tip1')}</li>
                          <li>{translate('voice.audio_quality_tip2')}</li>
                          <li>{translate('voice.audio_quality_tip3')}</li>
                          <li>{translate('voice.audio_quality_tip4')}</li>
                          <li>{translate('voice.audio_quality_tip5')}</li>
                          <li>{translate('voice.audio_quality_tip6')}</li>
                        </ul>
                        <div>
                          （3）<strong>{translate('voice.audio_content')}：</strong>
                        </div>
                        <ul>
                          <li>
                            {translate('voice.audio_content_tip1')}
                            <span className="red">{translate('voice.audio_content_tip2')}</span>
                          </li>
                        </ul>
                        <div>
                          （4）<strong>{translate('voice.audio_format')}：</strong>
                        </div>
                        <ul>
                          <li>{translate('voice.audio_format_tip')}</li>
                        </ul>
                      </div>
                    }
                    okText={translate('system.i_know')}
                    showCancel={false}
                  >
                    <ExclamationCircleFilled />
                  </Popconfirm>
                </div>

                {!previeUrl && !previewLoading && percent === -1 && (
                  <div className="warning">
                    <h4>{translate('voice.audio_requirements')}</h4>
                    <div className="desc">
                      <div>
                        <label className="label">{translate('voice.file_format')}：</label>
                        <label>{translate('voice.supported_formats')}</label>
                      </div>
                      <div>
                        <label className="label">{translate('voice.audio_duration')}：</label>
                        <label>{translate('voice.duration_range')}</label>
                      </div>
                      <div>
                        <label className="label">{translate('voice.audio_size')}：</label>
                        <label>{translate('voice.size_limit')}</label>
                      </div>
                    </div>
                  </div>
                )}

                {percent === -1 && (
                  <Dragger
                    accept=".mp3,.m4a,.wav"
                    showUploadList={false}
                    beforeUpload={async (file) => {
                      const flag = (await beforeAudioUpload(file)) as any
                      if (flag) {
                        setAudioUrl(URL.createObjectURL(file))
                        uploadFile(file)
                      }
                      return flag
                    }}
                    onDrop={(e) => onDrop(e.dataTransfer.files?.[0] as any)}
                  >
                    <p className="ant-upload-drag-icon">
                      <CloudUploadOutlined />
                    </p>
                    <p className="ant-upload-text">{translate('voice.upload_audio_prompt')}</p>
                    <p className="ant-upload-hint">{translate('system.upload_hint')}</p>
                  </Dragger>
                )}

                {percent > -1 && percent < 100 && (
                  <div className="step-progress">
                    <div className="step-progress-content">
                      <div className="percent">{percent}%</div>
                      <Progress percent={percent} showInfo={false} />
                      <div className="tips">{translate('voice.audio_uploading')}</div>
                    </div>
                    <div className="btns">
                      <Button onClick={cancelUpload}>{translate('system.cancel_upload')}</Button>
                    </div>
                  </div>
                )}

                {percent === 100 && (
                  <div className="step-view">
                    <div className="step-view-box">
                      <audio controls src={audioUrl} />
                      {!previeUrl && !previewLoading && (
                        <div
                          className="trash"
                          onClick={() => {
                            eventTracking('ReuploadClick')
                            resetUploadAudio()
                          }}
                        >
                          <DeleteOutlined />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {model === 1 && (
                  <>
                    {previewLoading && (
                      <div className="step-preview">
                        <div className="preview-title">
                          {translate('voice.preview_effect')}
                          <Popconfirm
                            title={translate('system.tip')}
                            description={<div style={{ width: 350 }}>{translate('voice.preview_effect_message')}</div>}
                            okText={translate('system.i_know')}
                            showCancel={false}
                          >
                            <ExclamationCircleFilled />
                          </Popconfirm>
                        </div>
                        <div className="preview-box">
                          <LoadingOutlined />
                          <div>{translate('voice.generating_preview')}</div>
                        </div>
                      </div>
                    )}
                    {previeUrl && (
                      <div className="step-view">
                        <div className="step-view-title">
                          {translate('voice.preview_effect')}
                          <Popconfirm
                            title={translate('system.tip')}
                            description={<div style={{ width: 350 }}>{translate('voice.preview_effect_message')}</div>}
                            okText={translate('system.i_know')}
                            showCancel={false}
                          >
                            <ExclamationCircleFilled />
                          </Popconfirm>
                        </div>
                        <div className="step-view-box">
                          <audio ref={audioRef} controls src={previeUrl} />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {!previeUrl && !previewLoading && (
                <div className="form-item">
                  <div className="title">{translate('voice.select_voice_model')}</div>
                  <Radio.Group onChange={onModelChange} value={model}>
                    <Radio value={2}>{translate('voice.premium_v2')}</Radio>
                    <Radio value={1}>{translate('voice.premium_v1')}</Radio>
                  </Radio.Group>
                </div>
              )}
            </>
          )}
        </div>
      </Drawer>
      <PayPointModal open={payPointModalOpen} onCancel={() => setPayPointModalOpen(false)} />
    </Suspense>
  )
}

export default memo(CloneHighDrawer)
